import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Typography,
  Alert,
  Tabs,
  Tab,
  FormControlLabel,
  Checkbox,
  Grid,
  Slider,
} from '@mui/material';
import { Save, Refresh } from '@mui/icons-material';
import { dhis2ConfigAPI, systemConfigAPI, type Dhis2ConfigOut } from '../../services/api';

const CONFIG_KEYS = {
  llm: 'llm',
  asr: 'asr_whisper',
  diarization: 'diarization',
  resources: 'resources',
  locale: 'locale',
  audio: 'audio',
  deployment: 'deployment',
} as const;

const LOCALE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'hi', label: 'Hindi' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'ar', label: 'Arabic' },
];

const LLM_PROVIDERS = [
  { value: 'groq', label: 'Groq' },
  { value: 'ollama', label: 'Ollama' },
  { value: 'openai', label: 'OpenAI' },
  { value: 'anthropic', label: 'Anthropic' },
] as const;

const defaultLlm: Record<string, unknown> = {
  provider: 'groq',
  ollama_base_url: 'http://localhost:11434',
  ollama_model: 'llama3.2:3b',
  groq_api_key: '',
  groq_model: 'qwen/qwen3-32b',
  openai_api_key: '',
  anthropic_api_key: '',
  api_key: '', // unified key when using one field per provider
  groq_max_tokens: 4096,
  groq_temperature: 0.2,
  model: '', // current selected model (used by backend when set)
  system_prompt: 'You are a medical AI assistant that extracts structured information from diabetes consultation transcripts. Always respond with valid JSON only. Use ONLY these section IDs: tab1_registration, tab2_consultation.',
  llm_validation_enabled: true,
  llm_validation_mode: 'async',
  llm_validation_timeout: 10,
};

const WHISPER_MODELS = [
  'tiny', 'tiny.en', 'base', 'base.en', 'small', 'small.en',
  'medium', 'medium.en', 'large', 'large-v2', 'large-v3',
];
const ASR_POLICY_OPTIONS = [
  { value: 'simulstreaming', label: 'Simulstreaming' },
  { value: 'localagreement', label: 'Local agreement' },
];
const DIARIZATION_BACKEND_OPTIONS = [
  { value: 'sortformer', label: 'Sortformer (NeMo)' },
  { value: 'diart', label: 'Diart' },
];

// WhisperLiveKit transcription languages (https://github.com/QuentinFuxa/WhisperLiveKit/blob/main/docs/supported_languages.md)
const WLK_TRANSCRIPTION_LANGUAGES: { value: string; label: string }[] = [
  { value: 'auto', label: 'Auto-detect' },
  { value: 'en', label: 'English' },
  { value: 'zh', label: 'Chinese' },
  { value: 'de', label: 'German' },
  { value: 'es', label: 'Spanish' },
  { value: 'ru', label: 'Russian' },
  { value: 'ko', label: 'Korean' },
  { value: 'fr', label: 'French' },
  { value: 'ja', label: 'Japanese' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'tr', label: 'Turkish' },
  { value: 'pl', label: 'Polish' },
  { value: 'ca', label: 'Catalan' },
  { value: 'nl', label: 'Dutch' },
  { value: 'ar', label: 'Arabic' },
  { value: 'sv', label: 'Swedish' },
  { value: 'it', label: 'Italian' },
  { value: 'id', label: 'Indonesian' },
  { value: 'hi', label: 'Hindi' },
  { value: 'fi', label: 'Finnish' },
  { value: 'vi', label: 'Vietnamese' },
  { value: 'he', label: 'Hebrew' },
  { value: 'uk', label: 'Ukrainian' },
  { value: 'el', label: 'Greek' },
  { value: 'ms', label: 'Malay' },
  { value: 'cs', label: 'Czech' },
  { value: 'ro', label: 'Romanian' },
  { value: 'da', label: 'Danish' },
  { value: 'hu', label: 'Hungarian' },
  { value: 'ta', label: 'Tamil' },
  { value: 'no', label: 'Norwegian' },
  { value: 'th', label: 'Thai' },
  { value: 'ur', label: 'Urdu' },
  { value: 'hr', label: 'Croatian' },
  { value: 'bg', label: 'Bulgarian' },
  { value: 'lt', label: 'Lithuanian' },
  { value: 'la', label: 'Latin' },
  { value: 'mi', label: 'Maori' },
  { value: 'ml', label: 'Malayalam' },
  { value: 'cy', label: 'Welsh' },
  { value: 'sk', label: 'Slovak' },
  { value: 'te', label: 'Telugu' },
  { value: 'fa', label: 'Persian' },
  { value: 'lv', label: 'Latvian' },
  { value: 'bn', label: 'Bengali' },
  { value: 'sr', label: 'Serbian' },
  { value: 'az', label: 'Azerbaijani' },
  { value: 'sl', label: 'Slovenian' },
  { value: 'kn', label: 'Kannada' },
  { value: 'et', label: 'Estonian' },
  { value: 'mk', label: 'Macedonian' },
  { value: 'br', label: 'Breton' },
  { value: 'eu', label: 'Basque' },
  { value: 'is', label: 'Icelandic' },
  { value: 'hy', label: 'Armenian' },
  { value: 'ne', label: 'Nepali' },
  { value: 'mn', label: 'Mongolian' },
  { value: 'bs', label: 'Bosnian' },
  { value: 'kk', label: 'Kazakh' },
  { value: 'sq', label: 'Albanian' },
  { value: 'sw', label: 'Swahili' },
  { value: 'gl', label: 'Galician' },
  { value: 'mr', label: 'Marathi' },
  { value: 'pa', label: 'Punjabi' },
  { value: 'si', label: 'Sinhala' },
  { value: 'km', label: 'Khmer' },
  { value: 'sn', label: 'Shona' },
  { value: 'yo', label: 'Yoruba' },
  { value: 'so', label: 'Somali' },
  { value: 'af', label: 'Afrikaans' },
  { value: 'oc', label: 'Occitan' },
  { value: 'ka', label: 'Georgian' },
  { value: 'be', label: 'Belarusian' },
  { value: 'tg', label: 'Tajik' },
  { value: 'sd', label: 'Sindhi' },
  { value: 'gu', label: 'Gujarati' },
  { value: 'am', label: 'Amharic' },
  { value: 'yi', label: 'Yiddish' },
  { value: 'lo', label: 'Lao' },
  { value: 'uz', label: 'Uzbek' },
  { value: 'fo', label: 'Faroese' },
  { value: 'ht', label: 'Haitian Creole' },
  { value: 'ps', label: 'Pashto' },
  { value: 'tk', label: 'Turkmen' },
  { value: 'nn', label: 'Nynorsk' },
  { value: 'mt', label: 'Maltese' },
  { value: 'sa', label: 'Sanskrit' },
  { value: 'lb', label: 'Luxembourgish' },
  { value: 'my', label: 'Myanmar' },
  { value: 'bo', label: 'Tibetan' },
  { value: 'tl', label: 'Tagalog' },
  { value: 'mg', label: 'Malagasy' },
  { value: 'as', label: 'Assamese' },
  { value: 'tt', label: 'Tatar' },
  { value: 'haw', label: 'Hawaiian' },
  { value: 'ln', label: 'Lingala' },
  { value: 'ha', label: 'Hausa' },
  { value: 'ba', label: 'Bashkir' },
  { value: 'jw', label: 'Javanese' },
  { value: 'su', label: 'Sundanese' },
  { value: 'yue', label: 'Cantonese' },
];
// Target language for NLLW translation (none = no translation; default English for output)
const WLK_TARGET_LANGUAGE_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'No translation (transcribe only)' },
  ...WLK_TRANSCRIPTION_LANGUAGES.filter((o) => o.value !== 'auto'),
];

const ASR_ENGINE_OPTIONS = [
  { value: 'whisperlivekit', label: 'WhisperLiveKit' },
  { value: 'indic_whisper', label: 'IndicWhisper (Indian languages)' },
  { value: 'sarvam_ai', label: 'Sarvam AI (Indian languages, ASR + translation)' },
];

const WLK_BACKEND_OPTIONS = [
  { value: 'auto', label: 'Auto (MLX on macOS else faster-whisper)' },
  { value: 'faster-whisper', label: 'faster-whisper (GPU)' },
  { value: 'mlx', label: 'mlx (legacy)' },
  { value: 'mlx-whisper', label: 'mlx-whisper (Apple Silicon)' },
  { value: 'whisper', label: 'whisper (vanilla)' },
  { value: 'openai-api', label: 'openai-api (LocalAgreement only)' },
  { value: 'voxtral', label: 'Voxtral (multilingual, auto-detect; Linux/GPU)' },
  { value: 'voxtral-mlx', label: 'Voxtral MLX (Apple Silicon)' },
];

const defaultAsr = {
  use_whisperlivekit: true,
  asr_engine: 'whisperlivekit',
  model: 'medium',
  language: 'auto',
  target_language: '',
  nllb_backend: 'ctranslate2',
  nllb_size: '1.3B',
  backend: 'faster-whisper',
  policy: 'simulstreaming',
  enable_vad: true,
  enable_vac: true,
  min_chunk: 1.6,
  beam_size: 1,
  diarization: true,
  diarization_backend: 'sortformer',
  diarization_segmentation_model: 'pyannote/segmentation-3.0',
  diarization_embedding_model: 'pyannote/embedding',
  diarization_disable_punctuation_split: false,
  diarization_step: 1.0,
  diarization_length: 4.0,
  pcm_input: true,
  vac_chunk_size: 0.8,
  buffer_trimming: 'sentence',
  buffer_trimming_sec: 5,
  decoder_type: 'greedy',
  max_context_tokens: 0,
  frame_threshold: 50,
  audio_max_len: 15,
  init_prompt: 'Conversation may contain English, Hindi, Telugu or Hinglish.',
  indic_whisper_model: 'ai4bharat/indic_whisper',
  indic_whisper_device: 'cuda',
  indic_whisper_chunk_duration_sec: 4,
  indic_whisper_language: null as string | null,
  indic_whisper_use_jax: false,
  sarvam_api_key: '',
  sarvam_base_url: 'https://api.sarvam.ai',
  sarvam_mode: 'translate',
  sarvam_language_code: 'unknown',
  sarvam_model: 'saaras:v3',
  sarvam_with_diarization: true,
  sarvam_num_speakers: null as number | null,
};

const NLLB_BACKEND_OPTIONS = [
  { value: 'transformers', label: 'Transformers' },
  { value: 'ctranslate2', label: 'CTranslate2 (faster)' },
];
const NLLB_SIZE_OPTIONS = [
  { value: '600M', label: '600M (faster, lighter)' },
  { value: '1.3B', label: '1.3B (better quality)' },
];

const defaultDiarization: Record<string, unknown> = {
  mode: 'light',
  vad_aggressiveness: 3,
  speaker_consistency_window: 5,
  reidentification_threshold: 0.75,
  reidentification_window: 3.0,
  min_segment_length: 0.5,
  speaker_stickiness: 0.7,
  num_speakers: null,
  enable_speaker_consistency_filter: true,
  enable_speaker_reidentification: true,
};

const defaultResources = {
  max_sessions: 3,
  max_gpu_asr_workers: 2,
  max_pending_llm: 5,
  degradation_enabled: true,
  memory_threshold_pct: 80,
  llm_validation_enabled: true,
};

const defaultAudio: Record<string, unknown> = {
  storage_path: 'storage/audio',
  storage_mode: 'hybrid',
  sample_rate: 16000,
  channels: 1,
};

const defaultDeployment: Record<string, unknown> = {
  deployment_mode: 'laptop',
  cabin_config_path: 'config/cabin_assignments.yaml',
  jetson_ip: '',
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} id={`system-ai-tab-${index}`} {...other}>
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

export default function SystemAiConfig() {
  const [tab, setTab] = useState(0);
  const [asrScope, setAsrScope] = useState<'global' | 'web_app' | 'dhis2' | 'dhis2_config' | 'web_app_config'>('global');
  const [allConfigs, setAllConfigs] = useState<Dhis2ConfigOut[]>([]);
  const [asrConfigId, setAsrConfigId] = useState<string>('');
  const [llm, setLlm] = useState<Record<string, unknown>>(defaultLlm);
  const [asr, setAsr] = useState<Record<string, unknown>>(defaultAsr);
  const [diarization, setDiarization] = useState<Record<string, unknown>>(defaultDiarization);
  const [resources, setResources] = useState<Record<string, unknown>>(defaultResources);
  const [audio, setAudio] = useState<Record<string, unknown>>(defaultAudio);
  const [deployment, setDeployment] = useState<Record<string, unknown>>(defaultDeployment);
  const [locale, setLocale] = useState<string>('en');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [llmModels, setLlmModels] = useState<string[]>([]);
  const [fetchModelsLoading, setFetchModelsLoading] = useState(false);
  const [fetchModelsError, setFetchModelsError] = useState<string | null>(null);
  const [reloadAsrLoading, setReloadAsrLoading] = useState(false);
  const [reloadAsrError, setReloadAsrError] = useState<string | null>(null);

  const isWebAdapterConfig = (cfg: Dhis2ConfigOut): boolean => {
    const schemaSource = String(cfg.schema_source ?? '').toLowerCase();
    const configType = String((cfg as unknown as { config_type?: string }).config_type ?? '').toLowerCase();
    return schemaSource === 'admin_defined' || configType === 'dynamic_form';
  };

  const webAdapterConfigs = useMemo(
    () => allConfigs.filter((cfg) => isWebAdapterConfig(cfg)),
    [allConfigs],
  );
  const dhis2OnlyConfigs = useMemo(
    () => allConfigs.filter((cfg) => !isWebAdapterConfig(cfg)),
    [allConfigs],
  );

  const loadAsrByScope = async () => {
    try {
      if (asrScope === 'dhis2_config') {
        if (!asrConfigId) return defaultAsr;
        const res = await systemConfigAPI.getAsrDhis2Config(asrConfigId);
        return (res.data?.config_value_json ?? defaultAsr) as Record<string, unknown>;
      }
      if (asrScope === 'web_app_config') {
        if (!asrConfigId) return defaultAsr;
        const res = await systemConfigAPI.getAsrWebAppConfig(asrConfigId);
        return (res.data?.config_value_json ?? defaultAsr) as Record<string, unknown>;
      }
      const res = await systemConfigAPI.getAsrScoped(asrScope);
      return (res.data?.config_value_json ?? defaultAsr) as Record<string, unknown>;
    } catch {
      return defaultAsr;
    }
  };

  const loadAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [l, a, d, r, loc, aud, dep] = await Promise.all([
        systemConfigAPI.get(CONFIG_KEYS.llm).then((res) => res.data?.config_value_json ?? defaultLlm).catch(() => defaultLlm),
        loadAsrByScope(),
        systemConfigAPI.get(CONFIG_KEYS.diarization).then((res) => res.data?.config_value_json ?? defaultDiarization).catch(() => defaultDiarization),
        systemConfigAPI.get(CONFIG_KEYS.resources).then((res) => res.data?.config_value_json ?? defaultResources).catch(() => defaultResources),
        systemConfigAPI.get(CONFIG_KEYS.locale).then((res) => (res.data?.config_value_json as { locale?: string })?.locale ?? 'en').catch(() => 'en'),
        systemConfigAPI.get(CONFIG_KEYS.audio).then((res) => res.data?.config_value_json ?? defaultAudio).catch(() => defaultAudio),
        systemConfigAPI.get(CONFIG_KEYS.deployment).then((res) => res.data?.config_value_json ?? defaultDeployment).catch(() => defaultDeployment),
      ]);
      setLlm({ ...defaultLlm, ...l } as Record<string, unknown>);
      setAsr({ ...defaultAsr, ...a } as Record<string, unknown>);
      setDiarization({ ...defaultDiarization, ...d } as Record<string, unknown>);
      setResources({ ...defaultResources, ...r } as Record<string, unknown>);
      setLocale(typeof loc === 'string' ? loc : 'en');
      setAudio({ ...defaultAudio, ...aud } as Record<string, unknown>);
      setDeployment({ ...defaultDeployment, ...dep } as Record<string, unknown>);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load system config');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, [asrScope, asrConfigId]);

  useEffect(() => {
    let cancelled = false;
    const loadConfigs = async () => {
      try {
        const res = await dhis2ConfigAPI.listConfigs();
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : [];
        setAllConfigs(list);
        if (!asrConfigId && list.length > 0) {
          setAsrConfigId(list[0].id);
        }
      } catch {
        if (!cancelled) setAllConfigs([]);
      }
    };
    void loadConfigs();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (asrScope === 'dhis2_config') {
      if (!dhis2OnlyConfigs.find((cfg) => cfg.id === asrConfigId)) {
        setAsrConfigId(dhis2OnlyConfigs[0]?.id ?? '');
      }
      return;
    }
    if (asrScope === 'web_app_config') {
      if (!webAdapterConfigs.find((cfg) => cfg.id === asrConfigId)) {
        setAsrConfigId(webAdapterConfigs[0]?.id ?? '');
      }
    }
  }, [asrScope, asrConfigId, dhis2OnlyConfigs, webAdapterConfigs]);

  const saveLlm = async () => {
    setSaving(true);
    setError(null);
    try {
      await systemConfigAPI.set(CONFIG_KEYS.llm, llm);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const saveAsr = async () => {
    setSaving(true);
    setError(null);
    try {
      if (asrScope === 'dhis2_config') {
        if (!asrConfigId) {
          setError('Please select a DHIS2 config first');
          return;
        }
        await systemConfigAPI.setAsrDhis2Config(asrConfigId, asr);
      } else if (asrScope === 'web_app_config') {
        if (!asrConfigId) {
          setError('Please select a web adapter config first');
          return;
        }
        await systemConfigAPI.setAsrWebAppConfig(asrConfigId, asr);
      } else {
        await systemConfigAPI.setAsrScoped(asrScope, asr);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const reloadAsr = async () => {
    setReloadAsrLoading(true);
    setReloadAsrError(null);
    try {
      await systemConfigAPI.reloadAsr();
    } catch (e: unknown) {
      setReloadAsrError(e instanceof Error ? e.message : 'Reload failed');
    } finally {
      setReloadAsrLoading(false);
    }
  };

  const saveDiarization = async () => {
    setSaving(true);
    setError(null);
    try {
      await systemConfigAPI.set(CONFIG_KEYS.diarization, diarization);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const saveResources = async () => {
    setSaving(true);
    setError(null);
    try {
      await systemConfigAPI.set(CONFIG_KEYS.resources, resources);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const saveAudio = async () => {
    setSaving(true);
    setError(null);
    try {
      await systemConfigAPI.set(CONFIG_KEYS.audio, audio);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const saveDeployment = async () => {
    setSaving(true);
    setError(null);
    try {
      await systemConfigAPI.set(CONFIG_KEYS.deployment, deployment);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const saveLocale = async () => {
    setSaving(true);
    setError(null);
    try {
      await systemConfigAPI.set(CONFIG_KEYS.locale, { locale });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const fetchLlmModels = async () => {
    const provider = String(llm.provider ?? 'groq');
    setFetchModelsLoading(true);
    setFetchModelsError(null);
    try {
      const body: { provider: string; api_key?: string; base_url?: string } = { provider };
      if (provider === 'ollama') {
        body.base_url = String(llm.ollama_base_url ?? 'http://localhost:11434').trim() || undefined;
      } else {
        const key = String(llm.groq_api_key ?? llm.api_key ?? '').trim()
          || (provider === 'openai' ? String(llm.openai_api_key ?? '').trim() : '')
          || (provider === 'anthropic' ? String(llm.anthropic_api_key ?? '').trim() : '');
        body.api_key = key || undefined;
      }
      const res = await systemConfigAPI.listLlmModels(body);
      setLlmModels(res.data?.models ?? []);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to fetch models';
      setFetchModelsError(String(msg));
      setLlmModels([]);
    } finally {
      setFetchModelsLoading(false);
    }
  };

  const update = (setter: React.Dispatch<React.SetStateAction<Record<string, unknown>>>, key: string, value: unknown) => {
    setter((prev) => ({ ...prev, [key]: value }));
  };

  if (loading) return <Typography>Loading…</Typography>;

  return (
    <Box>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        System / AI Configuration (LLM, ASR, Diarization, Resources). Stored in system_config. For ASR/NLLB: save then click &quot;Reload ASR&quot; in the ASR tab to apply without restart; other changes may require backend restart.
      </Typography>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom fontWeight={600}>Language / Locale (i18n)</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Locale</InputLabel>
            <Select value={locale} label="Locale" onChange={(e) => setLocale(e.target.value)}>
              {LOCALE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" size="small" onClick={saveLocale} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>Used for ASR and prompts when supported by backend.</Typography>
      </Paper>

      <Paper variant="outlined" sx={{ p: 1 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="LLM" id="system-ai-tab-0" />
          <Tab label="ASR Engine" id="system-ai-tab-1" />
          <Tab label="Diarization" id="system-ai-tab-2" />
          <Tab label="Resources" id="system-ai-tab-3" />
          <Tab label="Audio" id="system-ai-tab-4" />
          <Tab label="Deployment" id="system-ai-tab-5" />
        </Tabs>

        <TabPanel value={tab} index={0}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These settings are used by the app for extraction (transcript → form). They override .env when saved here.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Provider</InputLabel>
                <Select
                  value={String(llm.provider ?? 'groq')}
                  label="Provider"
                  onChange={(e) => { update(setLlm, 'provider', e.target.value); setLlmModels([]); }}
                >
                  {LLM_PROVIDERS.map((p) => (
                    <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {String(llm.provider ?? 'groq') === 'ollama' ? (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Ollama base URL"
                  placeholder="http://localhost:11434"
                  value={String(llm.ollama_base_url ?? '')}
                  onChange={(e) => update(setLlm, 'ollama_base_url', e.target.value)}
                />
              </Grid>
            ) : (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="password"
                  label="Provider API key"
                  placeholder={String(llm.provider) === 'groq' ? 'Groq API key' : 'API key (leave blank to keep)'}
                  value={String(
                    llm.provider === 'openai' ? (llm.openai_api_key ?? '') :
                    llm.provider === 'anthropic' ? (llm.anthropic_api_key ?? '') :
                    (llm.groq_api_key ?? llm.api_key ?? '')
                  )}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (llm.provider === 'openai') update(setLlm, 'openai_api_key', v);
                    else if (llm.provider === 'anthropic') update(setLlm, 'anthropic_api_key', v);
                    else { update(setLlm, 'groq_api_key', v); update(setLlm, 'api_key', v); }
                  }}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Model</InputLabel>
                <Select
                  value={
                    String(llm.provider) === 'ollama'
                      ? (String(llm.ollama_model || llm.model || '').trim() || ' ')
                      : (String(llm.groq_model || llm.model || '').trim() || ' '
                  )}
                  label="Model"
                  onChange={(e) => {
                    const v = e.target.value === ' ' ? '' : e.target.value;
                    if (llm.provider === 'ollama') { update(setLlm, 'ollama_model', v); update(setLlm, 'model', v); }
                    else { update(setLlm, 'groq_model', v); update(setLlm, 'model', v); }
                  }}
                  displayEmpty
                  renderValue={(v) => (v === ' ' ? '— Select or type below —' : v)}
                >
                  <MenuItem value=" ">— Select or type below —</MenuItem>
                  {llmModels.map((m) => (
                    <MenuItem key={m} value={m}>{m}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Refresh />}
                onClick={fetchLlmModels}
                disabled={fetchModelsLoading || (String(llm.provider) === 'ollama' ? !String(llm.ollama_base_url || '').trim() : !String(llm.groq_api_key || llm.api_key || '').trim())}
              >
                {fetchModelsLoading ? 'Fetching…' : 'Fetch models'}
              </Button>
              {fetchModelsError && (
                <Typography variant="caption" color="error" sx={{ ml: 1 }}>{fetchModelsError}</Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Model name (or choose from list above)"
                placeholder={String(llm.provider) === 'ollama' ? 'e.g. llama3.2:3b' : 'e.g. qwen/qwen3-32b'}
                value={String(llm.provider === 'ollama' ? (llm.ollama_model || llm.model) : (llm.groq_model || llm.model))}
                onChange={(e) => {
                  const v = e.target.value;
                  if (llm.provider === 'ollama') { update(setLlm, 'ollama_model', v); update(setLlm, 'model', v); }
                  else { update(setLlm, 'groq_model', v); update(setLlm, 'model', v); }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Max tokens"
                value={Number(llm.groq_max_tokens ?? llm.max_tokens ?? 4096)}
                onChange={(e) => { const n = Number(e.target.value) || 4096; update(setLlm, 'groq_max_tokens', n); update(setLlm, 'max_tokens', n); }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                type="number"
                inputProps={{ step: 0.1, min: 0, max: 2 }}
                label="Temperature"
                value={Number(llm.groq_temperature ?? llm.temperature ?? 0.2)}
                onChange={(e) => { const n = Number(e.target.value); update(setLlm, 'groq_temperature', Number.isFinite(n) ? n : 0.2); update(setLlm, 'temperature', Number.isFinite(n) ? n : 0.2); }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                size="small"
                label="System prompt"
                placeholder="Optional system message for the LLM (extraction context)."
                value={String(llm.system_prompt ?? '')}
                onChange={(e) => update(setLlm, 'system_prompt', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={llm.llm_validation_enabled !== false}
                    onChange={(e) => update(setLlm, 'llm_validation_enabled', e.target.checked)}
                  />
                }
                label="LLM validation enabled"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Validation mode</InputLabel>
                <Select
                  value={String(llm.llm_validation_mode ?? 'async')}
                  label="Validation mode"
                  onChange={(e) => update(setLlm, 'llm_validation_mode', e.target.value)}
                >
                  <MenuItem value="async">Async</MenuItem>
                  <MenuItem value="blocking">Blocking</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Validation timeout (s)"
                inputProps={{ min: 5, max: 60 }}
                value={Number(llm.llm_validation_timeout ?? 10)}
                onChange={(e) => update(setLlm, 'llm_validation_timeout', Number(e.target.value) || 10)}
              />
            </Grid>
          </Grid>
          <Button variant="contained" startIcon={<Save />} onClick={saveLlm} disabled={saving} sx={{ mt: 2 }}>
            {saving ? 'Saving…' : 'Save LLM'}
          </Button>
        </TabPanel>

        <TabPanel value={tab} index={1}>
          <Alert severity="info" sx={{ mb: 2 }}>
            ASR and NLLB settings take effect after you click &quot;Reload ASR&quot; below (or restart the backend). New sessions will use the updated config.
          </Alert>
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 240 }}>
              <InputLabel>ASR scope</InputLabel>
              <Select
                value={asrScope}
                label="ASR scope"
                onChange={(e) => setAsrScope(e.target.value as 'global' | 'web_app' | 'dhis2' | 'dhis2_config' | 'web_app_config')}
              >
                <MenuItem value="global">Global default</MenuItem>
                <MenuItem value="web_app">Web form ambient</MenuItem>
                <MenuItem value="web_app_config">Web adapter per-config (config_id)</MenuItem>
                <MenuItem value="dhis2">DHIS2 default</MenuItem>
                <MenuItem value="dhis2_config">DHIS2 per-config (config_id)</MenuItem>
              </Select>
            </FormControl>
            {(asrScope === 'dhis2_config' || asrScope === 'web_app_config') && (
              <FormControl size="small" sx={{ minWidth: 280 }}>
                <InputLabel>{asrScope === 'dhis2_config' ? 'DHIS2 Config' : 'Web Adapter Config'}</InputLabel>
                <Select
                  value={asrConfigId}
                  label={asrScope === 'dhis2_config' ? 'DHIS2 Config' : 'Web Adapter Config'}
                  onChange={(e) => setAsrConfigId(String(e.target.value || ''))}
                >
                  {(asrScope === 'dhis2_config' ? dhis2OnlyConfigs : webAdapterConfigs).map((cfg) => (
                    <MenuItem key={cfg.id} value={cfg.id}>{cfg.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Transcription engine</InputLabel>
                <Select
                  value={String(asr.asr_engine ?? 'whisperlivekit')}
                  label="Transcription engine"
                  onChange={(e) => update(setAsr, 'asr_engine', e.target.value)}
                >
                  {ASR_ENGINE_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(asr.use_whisperlivekit)}
                    onChange={(e) => update(setAsr, 'use_whisperlivekit', e.target.checked)}
                  />
                }
                label="Use WhisperLiveKit (when engine is WhisperLiveKit)"
              />
            </Grid>
            {String(asr.asr_engine ?? 'whisperlivekit') === 'indic_whisper' && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">IndicWhisper options</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="IndicWhisper model"
                    placeholder="ai4bharat/indic_whisper"
                    value={String(asr.indic_whisper_model ?? 'ai4bharat/indic_whisper')}
                    onChange={(e) => update(setAsr, 'indic_whisper_model', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Device</InputLabel>
                    <Select
                      value={String(asr.indic_whisper_device ?? 'cuda')}
                      label="Device"
                      onChange={(e) => update(setAsr, 'indic_whisper_device', e.target.value)}
                    >
                      <MenuItem value="cuda">CUDA (GPU)</MenuItem>
                      <MenuItem value="cpu">CPU</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    inputProps={{ min: 1, max: 10, step: 0.5 }}
                    label="Chunk duration (s)"
                    value={Number(asr.indic_whisper_chunk_duration_sec ?? 4)}
                    onChange={(e) => update(setAsr, 'indic_whisper_chunk_duration_sec', Number(e.target.value) || 4)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Language (optional)</InputLabel>
                    <Select
                      value={asr.indic_whisper_language == null || asr.indic_whisper_language === '' ? ' ' : String(asr.indic_whisper_language)}
                      label="Language (optional)"
                      onChange={(e) => update(setAsr, 'indic_whisper_language', e.target.value === ' ' ? null : e.target.value)}
                      displayEmpty
                      renderValue={(v) => (v === ' ' ? 'Auto-detect' : v)}
                    >
                      <MenuItem value=" ">Auto-detect</MenuItem>
                      <MenuItem value="hi">Hindi</MenuItem>
                      <MenuItem value="te">Telugu</MenuItem>
                      <MenuItem value="ta">Tamil</MenuItem>
                      <MenuItem value="mr">Marathi</MenuItem>
                      <MenuItem value="bn">Bengali</MenuItem>
                      <MenuItem value="gu">Gujarati</MenuItem>
                      <MenuItem value="kn">Kannada</MenuItem>
                      <MenuItem value="ml">Malayalam</MenuItem>
                      <MenuItem value="en">English</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(asr.indic_whisper_use_jax)}
                        onChange={(e) => update(setAsr, 'indic_whisper_use_jax', e.target.checked)}
                      />
                    }
                    label="Use JAX (faster, optional)"
                  />
                </Grid>
              </>
            )}
            {String(asr.asr_engine ?? 'whisperlivekit') === 'sarvam_ai' && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Sarvam AI options</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    type="password"
                    label="API key"
                    placeholder="Sarvam API subscription key"
                    value={String(asr.sarvam_api_key ?? '')}
                    onChange={(e) => update(setAsr, 'sarvam_api_key', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Base URL"
                    placeholder="https://api.sarvam.ai"
                    value={String(asr.sarvam_base_url ?? 'https://api.sarvam.ai')}
                    onChange={(e) => update(setAsr, 'sarvam_base_url', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Mode</InputLabel>
                    <Select
                      value={String(asr.sarvam_mode ?? 'translate')}
                      label="Mode"
                      onChange={(e) => update(setAsr, 'sarvam_mode', e.target.value)}
                    >
                      <MenuItem value="transcribe">Transcribe (original language)</MenuItem>
                      <MenuItem value="translate">Translate (to English)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Input language code (BCP-47)"
                    placeholder="unknown, mr-IN, hi-IN, en-IN"
                    value={String(asr.sarvam_language_code ?? 'unknown')}
                    onChange={(e) => update(setAsr, 'sarvam_language_code', e.target.value)}
                    helperText="Use unknown (auto) or mr-IN for Marathi. Avoid en-IN for Marathi/Hindi speech or output may be Romanized."
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Sarvam model"
                    placeholder="saaras:v3"
                    value={String(asr.sarvam_model ?? 'saaras:v3')}
                    onChange={(e) => update(setAsr, 'sarvam_model', e.target.value)}
                    helperText="saaras:v3 recommended for native script (transcribe) vs translit (Roman)."
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Note: Speaker diarization is only supported in Sarvam Batch API; live/chunked API uses a single speaker.
                  </Typography>
                </Grid>
              </>
            )}
            {String(asr.asr_engine ?? 'whisperlivekit') === 'whisperlivekit' && (
              <>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Model</InputLabel>
                <Select
                  value={WHISPER_MODELS.includes(String(asr.model ?? '')) ? String(asr.model) : 'medium'}
                  label="Model"
                  onChange={(e) => update(setAsr, 'model', e.target.value)}
                >
                  {WHISPER_MODELS.map((m) => (
                    <MenuItem key={m} value={m}>{m}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Language (transcription)</InputLabel>
                <Select
                  value={WLK_TRANSCRIPTION_LANGUAGES.some((o) => o.value === (asr.language ?? 'en')) ? String(asr.language ?? 'en') : 'en'}
                  label="Language (transcription)"
                  onChange={(e) => update(setAsr, 'language', e.target.value)}
                >
                  {WLK_TRANSCRIPTION_LANGUAGES.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Target language (translation)</InputLabel>
                <Select
                  value={String(asr.target_language ?? '')}
                  label="Target language (translation)"
                  onChange={(e) => update(setAsr, 'target_language', e.target.value)}
                >
                  {WLK_TARGET_LANGUAGE_OPTIONS.map((o) => (
                    <MenuItem key={o.value || 'none'} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>NLLB backend (translation)</InputLabel>
                <Select
                  value={String(asr.nllb_backend ?? 'ctranslate2')}
                  label="NLLB backend (translation)"
                  onChange={(e) => update(setAsr, 'nllb_backend', e.target.value)}
                >
                  {NLLB_BACKEND_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>NLLB size (translation)</InputLabel>
                <Select
                  value={String(asr.nllb_size ?? '1.3B')}
                  label="NLLB size (translation)"
                  onChange={(e) => update(setAsr, 'nllb_size', e.target.value)}
                >
                  {NLLB_SIZE_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Backend</InputLabel>
                <Select value={String(asr.backend ?? 'faster-whisper')} label="Backend" onChange={(e) => update(setAsr, 'backend', e.target.value)}>
                  {WLK_BACKEND_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {['voxtral', 'voxtral-mlx'].includes(String(asr.backend ?? '')) && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Voxtral: multilingual with auto language detection. NLLB/translation options may not apply.</Typography>
              </Grid>
            )}
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Policy</InputLabel>
                <Select
                  value={String(asr.policy ?? 'simulstreaming')}
                  label="Policy"
                  onChange={(e) => update(setAsr, 'policy', e.target.value)}
                >
                  {ASR_POLICY_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={<Checkbox checked={Boolean(asr.enable_vad)} onChange={(e) => update(setAsr, 'enable_vad', e.target.checked)} />}
                label="VAD"
              />
              <FormControlLabel
                control={<Checkbox checked={Boolean(asr.enable_vac)} onChange={(e) => update(setAsr, 'enable_vac', e.target.checked)} />}
                label="VAC"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Diarization backend</InputLabel>
                <Select
                  value={String(asr.diarization_backend ?? 'sortformer')}
                  label="Diarization backend"
                  onChange={(e) => update(setAsr, 'diarization_backend', e.target.value)}
                >
                  {DIARIZATION_BACKEND_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {String(asr.diarization_backend ?? 'sortformer') === 'diart' && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Diart options (Hugging Face model IDs; may require HF login for gated models)</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Segmentation model"
                    placeholder="pyannote/segmentation-3.0"
                    value={String(asr.diarization_segmentation_model ?? 'pyannote/segmentation-3.0')}
                    onChange={(e) => update(setAsr, 'diarization_segmentation_model', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Embedding model"
                    placeholder="speechbrain/spkrec-ecapa-voxceleb"
                    value={String(asr.diarization_embedding_model ?? 'speechbrain/spkrec-ecapa-voxceleb')}
                    onChange={(e) => update(setAsr, 'diarization_embedding_model', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(asr.diarization_disable_punctuation_split)}
                        onChange={(e) => update(setAsr, 'diarization_disable_punctuation_split', e.target.checked)}
                      />
                    }
                    label="Disable punctuation split (may be non-functional in some WLK versions)"
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={<Checkbox checked={Boolean(asr.diarization)} onChange={(e) => update(setAsr, 'diarization', e.target.checked)} />}
                label="Diarization"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={<Checkbox checked={asr.pcm_input !== false} onChange={(e) => update(setAsr, 'pcm_input', e.target.checked)} />}
                label="PCM input (raw Int16)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" gutterBottom>Diarization step (s)</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Slider
                  size="small"
                  value={Number(asr.diarization_step ?? 1)}
                  min={0.25}
                  max={2}
                  step={0.25}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(v) => `${v}s`}
                  onChange={(_, value) => update(setAsr, 'diarization_step', value as number)}
                  sx={{ flex: 1, maxWidth: 280 }}
                />
                <TextField
                  size="small"
                  type="number"
                  inputProps={{ min: 0.25, max: 2, step: 0.25 }}
                  sx={{ width: 72 }}
                  value={Number(asr.diarization_step ?? 1)}
                  onChange={(e) => update(setAsr, 'diarization_step', Math.min(2, Math.max(0.25, Number(e.target.value) || 1)))}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" gutterBottom>Diarization length (s)</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Slider
                  size="small"
                  value={Number(asr.diarization_length ?? 4)}
                  min={1}
                  max={8}
                  step={0.5}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(v) => `${v}s`}
                  onChange={(_, value) => update(setAsr, 'diarization_length', value as number)}
                  sx={{ flex: 1, maxWidth: 280 }}
                />
                <TextField
                  size="small"
                  type="number"
                  inputProps={{ min: 1, max: 8, step: 0.5 }}
                  sx={{ width: 72 }}
                  value={Number(asr.diarization_length ?? 4)}
                  onChange={(e) => update(setAsr, 'diarization_length', Math.min(8, Math.max(1, Number(e.target.value) || 4)))}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                type="number"
                inputProps={{ step: 0.1, min: 0.05 }}
                label="Min chunk size (s)"
                value={Number(asr.min_chunk ?? 1.6)}
                onChange={(e) => update(setAsr, 'min_chunk', Number(e.target.value) || 1.6)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                type="number"
                inputProps={{ min: 1, max: 10 }}
                label="Beams"
                value={Number(asr.beam_size ?? 1)}
                onChange={(e) => update(setAsr, 'beam_size', Number(e.target.value) || 1)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                type="number"
                inputProps={{ step: 0.1, min: 0.01 }}
                label="VAC chunk size (s)"
                value={Number(asr.vac_chunk_size ?? 0.8)}
                onChange={(e) => update(setAsr, 'vac_chunk_size', Number(e.target.value) || 0.8)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Buffer trimming</InputLabel>
                <Select
                  value={String(asr.buffer_trimming ?? 'sentence')}
                  label="Buffer trimming"
                  onChange={(e) => update(setAsr, 'buffer_trimming', e.target.value)}
                >
                  <MenuItem value="sentence">sentence</MenuItem>
                  <MenuItem value="segment">segment</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                type="number"
                inputProps={{ min: 1, max: 30, step: 0.5 }}
                label="Buffer trimming (s)"
                value={Number(asr.buffer_trimming_sec ?? 5)}
                onChange={(e) => update(setAsr, 'buffer_trimming_sec', Number(e.target.value) || 5)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Decoder</InputLabel>
                <Select
                  value={String(asr.decoder_type ?? 'greedy')}
                  label="Decoder"
                  onChange={(e) => update(setAsr, 'decoder_type', e.target.value)}
                >
                  <MenuItem value="greedy">greedy</MenuItem>
                  <MenuItem value="beam">beam</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                type="number"
                inputProps={{ min: 0, max: 512 }}
                label="Max context tokens"
                value={Number(asr.max_context_tokens ?? 0)}
                onChange={(e) => update(setAsr, 'max_context_tokens', Number(e.target.value) || 0)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                type="number"
                inputProps={{ min: 1, max: 200 }}
                label="Frame threshold"
                value={Number(asr.frame_threshold ?? 50)}
                onChange={(e) => update(setAsr, 'frame_threshold', Number(e.target.value) || 50)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                type="number"
                inputProps={{ step: 0.5, min: 5, max: 60 }}
                label="Audio max len (s)"
                value={Number(asr.audio_max_len ?? 15)}
                onChange={(e) => update(setAsr, 'audio_max_len', Number(e.target.value) || 15)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={2}
                label="Init prompt"
                placeholder="Conversation may contain English, Hindi, Telugu or Hinglish."
                value={String(asr.init_prompt ?? '')}
                onChange={(e) => update(setAsr, 'init_prompt', e.target.value)}
              />
            </Grid>
              </>
            )}
          </Grid>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mt: 2 }}>
            <Button variant="contained" startIcon={<Save />} onClick={saveAsr} disabled={saving}>
              {saving ? 'Saving…' : 'Save ASR'}
            </Button>
            <Button variant="outlined" startIcon={<Refresh />} onClick={reloadAsr} disabled={reloadAsrLoading}>
              {reloadAsrLoading ? 'Reloading…' : 'Reload ASR'}
            </Button>
            {reloadAsrError && (
              <Typography variant="body2" color="error">{reloadAsrError}</Typography>
            )}
          </Box>
        </TabPanel>

        <TabPanel value={tab} index={2}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Mode</InputLabel>
                <Select
                  value={String(diarization.mode ?? 'light')}
                  label="Mode"
                  onChange={(e) => update(setDiarization, 'mode', e.target.value)}
                >
                  <MenuItem value="none">None</MenuItem>
                  <MenuItem value="light">Light</MenuItem>
                  <MenuItem value="full">Full (pyannote)</MenuItem>
                  <MenuItem value="nemo">NeMo (NVIDIA)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" display="block">
                Light = 2 speakers only (energy-based). For 3+ speakers or best accuracy use Full (pyannote) or NeMo.
                NeMo requires nemo_toolkit[asr] and config; use with Sarvam/IndicWhisper when built-in diarization is not used.
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" gutterBottom>VAD aggressiveness (0–3)</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Slider
                  size="small"
                  value={Number(diarization.vad_aggressiveness ?? 3)}
                  min={0}
                  max={3}
                  step={1}
                  marks={[{ value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }]}
                  valueLabelDisplay="auto"
                  onChange={(_, value) => update(setDiarization, 'vad_aggressiveness', value as number)}
                  sx={{ flex: 1, maxWidth: 240 }}
                />
                <TextField
                  size="small"
                  type="number"
                  inputProps={{ min: 0, max: 3, step: 1 }}
                  sx={{ width: 56 }}
                  value={Number(diarization.vad_aggressiveness ?? 3)}
                  onChange={(e) => update(setDiarization, 'vad_aggressiveness', Math.min(3, Math.max(0, Number(e.target.value) || 0)))}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" gutterBottom>Speaker consistency window</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Slider
                  size="small"
                  value={Number(diarization.speaker_consistency_window ?? 5)}
                  min={1}
                  max={20}
                  step={1}
                  valueLabelDisplay="auto"
                  onChange={(_, value) => update(setDiarization, 'speaker_consistency_window', value as number)}
                  sx={{ flex: 1, maxWidth: 240 }}
                />
                <TextField
                  size="small"
                  type="number"
                  inputProps={{ min: 1, max: 20 }}
                  sx={{ width: 56 }}
                  value={Number(diarization.speaker_consistency_window ?? 5)}
                  onChange={(e) => update(setDiarization, 'speaker_consistency_window', Math.min(20, Math.max(1, Number(e.target.value) || 5)))}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" gutterBottom>Reidentification threshold</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Slider
                  size="small"
                  value={Number(diarization.reidentification_threshold ?? 0.75)}
                  min={0.5}
                  max={1}
                  step={0.05}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(v) => v.toFixed(2)}
                  onChange={(_, value) => update(setDiarization, 'reidentification_threshold', value as number)}
                  sx={{ flex: 1, maxWidth: 240 }}
                />
                <TextField
                  size="small"
                  type="number"
                  inputProps={{ min: 0.5, max: 1, step: 0.05 }}
                  sx={{ width: 72 }}
                  value={Number(diarization.reidentification_threshold ?? 0.75)}
                  onChange={(e) => update(setDiarization, 'reidentification_threshold', Math.min(1, Math.max(0.5, Number(e.target.value) || 0.75)))}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                inputProps={{ step: 0.1, min: 0.1 }}
                label="Reidentification window (s)"
                value={Number(diarization.reidentification_window ?? 3)}
                onChange={(e) => update(setDiarization, 'reidentification_window', Number(e.target.value) || 3)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                inputProps={{ step: 0.1, min: 0.1 }}
                label="Min segment length (s)"
                value={Number(diarization.min_segment_length ?? 0.5)}
                onChange={(e) => update(setDiarization, 'min_segment_length', Number(e.target.value) || 0.5)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" gutterBottom>Speaker stickiness (0–1)</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Slider
                  size="small"
                  value={Number(diarization.speaker_stickiness ?? 0.7)}
                  min={0}
                  max={1}
                  step={0.1}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(v) => v.toFixed(1)}
                  onChange={(_, value) => update(setDiarization, 'speaker_stickiness', value as number)}
                  sx={{ flex: 1, maxWidth: 240 }}
                />
                <TextField
                  size="small"
                  type="number"
                  inputProps={{ min: 0, max: 1, step: 0.1 }}
                  sx={{ width: 72 }}
                  value={Number(diarization.speaker_stickiness ?? 0.7)}
                  onChange={(e) => update(setDiarization, 'speaker_stickiness', Math.min(1, Math.max(0, Number(e.target.value) || 0.7)))}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                inputProps={{ min: 0 }}
                label="Num speakers (0 = auto)"
                placeholder="Auto"
                value={diarization.num_speakers === undefined || diarization.num_speakers === null ? '' : Number(diarization.num_speakers)}
                onChange={(e) => {
                  const v = e.target.value.trim();
                  update(setDiarization, 'num_speakers', v === '' ? null : Number(v) || 0);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={diarization.enable_speaker_consistency_filter !== false}
                    onChange={(e) => update(setDiarization, 'enable_speaker_consistency_filter', e.target.checked)}
                  />
                }
                label="Speaker consistency filter"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={diarization.enable_speaker_reidentification !== false}
                    onChange={(e) => update(setDiarization, 'enable_speaker_reidentification', e.target.checked)}
                  />
                }
                label="Speaker reidentification"
              />
            </Grid>
          </Grid>
          <Button variant="contained" startIcon={<Save />} onClick={saveDiarization} disabled={saving} sx={{ mt: 2 }}>
            {saving ? 'Saving…' : 'Save Diarization'}
          </Button>
        </TabPanel>

        <TabPanel value={tab} index={3}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Resource limits and graceful degradation. Backend may require restart to apply.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" gutterBottom>Max concurrent sessions</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Slider
                  size="small"
                  value={Number(resources.max_sessions ?? 3)}
                  min={1}
                  max={20}
                  step={1}
                  valueLabelDisplay="auto"
                  onChange={(_, value) => update(setResources, 'max_sessions', value as number)}
                  sx={{ flex: 1, maxWidth: 280 }}
                />
                <TextField
                  size="small"
                  type="number"
                  inputProps={{ min: 1, max: 20 }}
                  sx={{ width: 64 }}
                  value={Number(resources.max_sessions ?? 3)}
                  onChange={(e) => update(setResources, 'max_sessions', Math.min(20, Math.max(1, Number(e.target.value) || 3)))}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" gutterBottom>Max GPU ASR workers</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Slider
                  size="small"
                  value={Number(resources.max_gpu_asr_workers ?? 2)}
                  min={1}
                  max={10}
                  step={1}
                  valueLabelDisplay="auto"
                  onChange={(_, value) => update(setResources, 'max_gpu_asr_workers', value as number)}
                  sx={{ flex: 1, maxWidth: 280 }}
                />
                <TextField
                  size="small"
                  type="number"
                  inputProps={{ min: 1, max: 10 }}
                  sx={{ width: 64 }}
                  value={Number(resources.max_gpu_asr_workers ?? 2)}
                  onChange={(e) => update(setResources, 'max_gpu_asr_workers', Math.min(10, Math.max(1, Number(e.target.value) || 2)))}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" gutterBottom>Max pending LLM tasks</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Slider
                  size="small"
                  value={Number(resources.max_pending_llm ?? 5)}
                  min={1}
                  max={20}
                  step={1}
                  valueLabelDisplay="auto"
                  onChange={(_, value) => update(setResources, 'max_pending_llm', value as number)}
                  sx={{ flex: 1, maxWidth: 280 }}
                />
                <TextField
                  size="small"
                  type="number"
                  inputProps={{ min: 1, max: 20 }}
                  sx={{ width: 64 }}
                  value={Number(resources.max_pending_llm ?? 5)}
                  onChange={(e) => update(setResources, 'max_pending_llm', Math.min(20, Math.max(1, Number(e.target.value) || 5)))}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" gutterBottom>Memory % threshold (degradation)</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Slider
                  size="small"
                  value={Number(resources.memory_threshold_pct ?? 80)}
                  min={50}
                  max={100}
                  step={5}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(v) => `${v}%`}
                  onChange={(_, value) => update(setResources, 'memory_threshold_pct', value as number)}
                  sx={{ flex: 1, maxWidth: 280 }}
                />
                <TextField
                  size="small"
                  type="number"
                  inputProps={{ min: 50, max: 100, step: 5 }}
                  sx={{ width: 64 }}
                  value={Number(resources.memory_threshold_pct ?? 80)}
                  onChange={(e) => update(setResources, 'memory_threshold_pct', Math.min(100, Math.max(50, Number(e.target.value) || 80)))}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(resources.degradation_enabled)}
                    onChange={(e) => update(setResources, 'degradation_enabled', e.target.checked)}
                  />
                }
                label="Degradation enabled (reduce load when memory exceeds threshold)"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={resources.llm_validation_enabled !== false}
                    onChange={(e) => update(setResources, 'llm_validation_enabled', e.target.checked)}
                  />
                }
                label="LLM validation enabled (async validation queue; used by extraction pipeline)"
              />
            </Grid>
          </Grid>
          <Button variant="contained" startIcon={<Save />} onClick={saveResources} disabled={saving} sx={{ mt: 2 }}>
            {saving ? 'Saving…' : 'Save Resources'}
          </Button>
        </TabPanel>

        <TabPanel value={tab} index={4}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Audio storage and playback. Used by transcription WS, audio playback API, and session manager.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Storage path"
                placeholder="storage/audio"
                value={String(audio.storage_path ?? 'storage/audio')}
                onChange={(e) => update(setAudio, 'storage_path', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Storage mode</InputLabel>
                <Select
                  value={String(audio.storage_mode ?? 'hybrid')}
                  label="Storage mode"
                  onChange={(e) => update(setAudio, 'storage_mode', e.target.value)}
                >
                  <MenuItem value="memory">Memory only</MenuItem>
                  <MenuItem value="disk">Disk only</MenuItem>
                  <MenuItem value="hybrid">Hybrid (session-level)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Sample rate"
                inputProps={{ min: 8000, max: 48000, step: 1000 }}
                value={Number(audio.sample_rate ?? 16000)}
                onChange={(e) => update(setAudio, 'sample_rate', Number(e.target.value) || 16000)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Channels"
                inputProps={{ min: 1, max: 2 }}
                value={Number(audio.channels ?? 1)}
                onChange={(e) => update(setAudio, 'channels', Number(e.target.value) || 1)}
              />
            </Grid>
          </Grid>
          <Button variant="contained" startIcon={<Save />} onClick={saveAudio} disabled={saving} sx={{ mt: 2 }}>
            {saving ? 'Saving…' : 'Save Audio'}
          </Button>
        </TabPanel>

        <TabPanel value={tab} index={5}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Deployment mode and paths. Used by device management, session manager, transcription WS.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Deployment mode</InputLabel>
                <Select
                  value={String(deployment.deployment_mode ?? 'laptop')}
                  label="Deployment mode"
                  onChange={(e) => update(setDeployment, 'deployment_mode', e.target.value)}
                >
                  <MenuItem value="laptop">Laptop</MenuItem>
                  <MenuItem value="jetson">Jetson</MenuItem>
                  <MenuItem value="server">Server</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Cabin config path"
                placeholder="config/cabin_assignments.yaml"
                value={String(deployment.cabin_config_path ?? '')}
                onChange={(e) => update(setDeployment, 'cabin_config_path', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Jetson IP (optional)"
                placeholder="192.168.2.62"
                value={String(deployment.jetson_ip ?? '')}
                onChange={(e) => update(setDeployment, 'jetson_ip', e.target.value)}
              />
            </Grid>
          </Grid>
          <Button variant="contained" startIcon={<Save />} onClick={saveDeployment} disabled={saving} sx={{ mt: 2 }}>
            {saving ? 'Saving…' : 'Save Deployment'}
          </Button>
        </TabPanel>
      </Paper>
    </Box>
  );
}
